/**
 * UserGradeListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  UserList,
} from 'components';
import { Avatar, Button, DatePicker, Radio, Table, Spin, Flex } from 'antd';
import { Link } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';
import { strCut, stringToMoneyFormat } from 'utils';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';

const INITIAL_VALUE = {
  grade_id: 0,
  point_type: '',
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
};

const TYPE_VAlUE = {
  EARNED: {
    color: 'blue',
    label: '적립',
  },
  REFUNDED: {
    color: 'magenta',
    label: '환불적립',
  },
  EXPIRED: {
    color: 'red',
    label: '만료',
  },
  USED: {
    color: 'orange',
    label: '사용',
  },
  DEDUCTED: {
    color: 'purple',
    label: '차감',
  },
};

/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 등급변동내역
 * ----------------------------------------
 */
const UserGradeListPresenter = ({
  userMemberShipList,
  gradeList,
  MemerShipGrade,
  permission,
  loading,
  currentUser,
  setCurrentUser,
  setPeriod,
  setLoading,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [filterItem, setFilterItem] = React.useState(INITIAL_VALUE);
  const [filterList, setFilterList] = React.useState(userMemberShipList);
  const [currentUserData, setCurrentUserData] = React.useState(null);
  const [summary, setSummary] = React.useState({});
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '현재 등급 유형',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.grade_id}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, grade_id: e.target.value }))
              }
            >
              <Radio value={0}>전체</Radio>
              {gradeList?.map((grade) => (
                <>
                  <Radio value={grade?.grade_id}>{grade?.grade_name}</Radio>
                </>
              ))}
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '변경일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={filterItem.value}
                onChange={(e) => {
                  setFilterItem((prev) => ({
                    ...prev,
                    value: e.target.value,
                    start_date: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end_date: dayjs().endOf('day'),
                  }));
                }}
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(filterItem.start_date)}
                  onChange={(e) =>
                    setFilterItem((prev) => ({
                      ...prev,
                      start_date: dayjs(e).startOf('day'),
                    }))
                  }
                  maxDate={filterItem.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(filterItem.end_date)}
                  onChange={(e) =>
                    setFilterItem((prev) => ({
                      ...prev,
                      end_date: dayjs(e).endOf('day'),
                    }))
                  }
                  minDate={filterItem.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  const col = [
    {
      title: '등급내역번호',
      dataIndex: 'membership_grade_id',
      key: 'membership_grade_id',
      width: 100,
      sorter: {
        compare: (a, b) => a.membership_grade_id - b.membership_grade_id,
        multiple: 2,
      },
      render: (membership_grade_id) => <>{membership_grade_id}</>,
    },
    {
      title: '이름(아이디)',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: () => (
        <>
          <Avatar style={{ marginRight: 5 }}>
            {currentUserData.user_name}
          </Avatar>
          <Link
            to={`/users/list?modal=${encodeURIComponent(
              encrypt(currentUserData.user_id)
            )}`}
          >
            {strCut(currentUserData.user_name, 10)}(
            {strCut(currentUserData.user_account, 10)})
          </Link>
        </>
      ),
    },
    {
      title: '핸드폰번호',
      dataIndex: 'user_phone',
      key: 'user_phone',
      width: 150,
      render: () => <>{currentUserData.user_phone}</>,
    },
    {
      title: '등급명',
      dataIndex: 'grade_name',
      key: 'grade_name',
      width: 150,
      render: (grade_name) => <>{grade_name}</>,
    },
    {
      title: '등급사유',
      dataIndex: 'grade_reason',
      key: 'grade_reason',
      width: 250,
      render: (grade_reason) => <>{grade_reason}</>,
    },
    {
      title: '변경일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (created_at) =>
        dayjs(created_at * 1000).format('YYYY-MM-DD(HH:mm)'),
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   * 등급 요약 계산
   * --
   * @returns
   */
  const calculateGrade = () => {
    const gradeSummary = gradeList?.reduce((acc, grade) => {
      const gradeNum = userMemberShipList?.reduce((acc, member) => {
        return member?.grade_id === grade?.grade_id ? ++acc : acc;
      }, 0);
      acc[grade?.grade_id] = gradeNum;
      return acc;
    }, {});
    setSummary(gradeSummary);
  };
  /* ===== FUNCTION ===== */
  /**
   * 포인트 조건 검색
   * --
   * @param {string} t
   */
  const handleSearch = (t) => {
    setLoading(true);
    //현재 회원 등급별 필터
    const newUserList = userMemberShipList?.filter((user) => {
      if (filterItem?.grade_id === 0) {
        return true;
      } else if (filterItem?.grade_id > 0) {
        return user?.grade_id === filterItem?.grade_id;
      }
    });
    const sort = newUserList?.map((user) => ({
      ...user,
      membership_grade: user?.membership_grade?.sort(
        (a, b) => b?.created_at - a?.created_at
      ),
    }));

    setFilterList(sort);
    setCurrentUser(null);
    setCurrentUserData(null);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  /* ===== Hooks ===== */
  //
  React.useEffect(() => {
    const filterUser = filterList?.find(
      (user) => user?.user_id === currentUser
    );
    setCurrentUserData(filterUser);
  }, [currentUser, filterList]);
  //
  React.useEffect(() => {
    calculateGrade();
  }, [currentUser, MemerShipGrade]);
  //
  React.useEffect(() => {
    handleSearch('search');
  }, [MemerShipGrade]);
  return (
    <>
      {/* === Loading - start === */}
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        {/* ===== 헤더&필터 - start ===== */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 타이틀 === */}
          <Title style={{ ...styles.title }}>등급 변동 내역</Title>

          {/* === 필터 - start === */}
          <ExpandableContainer borderRadius={4} padding={20}>
            {formItem.map((_item, _index) => (
              <div
                key={`form_${_index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 0px',
                }}
              >
                <div style={{ width: 120, fontSize: 13, fontWeight: 400 }}>
                  <label>{_item.label}</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {_item.form.map((_subitem, _subkey) => (
                    <React.Fragment key={`input_${_subkey}`}>
                      {_subitem.input}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableContainer>

          {/* 검색/초기화 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 13,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{
                ...styles.button,
                background: 'var(--primary-color)',
              }}
              onClick={() => {
                setPeriod({
                  start_date: filterItem.start_date.unix(),
                  end_date: filterItem.end_date.unix(),
                });
                handleSearch('search');
              }}
            >
              검색
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => {
                setPeriod({
                  start_date: INITIAL_VALUE.start_date.unix(),
                  end_date: INITIAL_VALUE.end_date.unix(),
                });
                setFilterItem(INITIAL_VALUE);
                handleSearch('reset');
              }}
            >
              초기화
            </Button>
          </div>
          {/* 검색/초기화 버튼 끝 */}
          {/* === 필터 - end === */}
        </Content>
        {/* ===== 헤더&필터 - end ===== */}

        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 통계 - start === */}
          <Content
            maxWidth={'100%'}
            padding={0}
            backgroundColor={'none'}
            style={{
              marginBottom: 4,
              display: 'flex',
              flexDirection: 'column', // 세로 배치
              gap: '2px',
            }}
          >
            {gradeList
              ?.map((grade) => ({
                title: grade?.grade_name,
                value: summary[grade?.grade_id] ?? 0,
                suffix: '명',
              }))
              ?.reduce((acc, cur, index) => {
                const rowIndex = Math.floor(index / 5);
                if (!acc[rowIndex]) acc[rowIndex] = [];
                acc[rowIndex].push(cur);
                return acc;
              }, [])
              ?.map((group, groupIndex) => (
                <div
                  key={`group-${groupIndex}`}
                  style={{
                    display: 'flex',
                    gap: '2px',
                  }}
                >
                  {group.map((_item, _index) => (
                    <div
                      key={`${_item.title}-${_index}`}
                      className="boxShadow"
                      style={{
                        background: '#FFFFFF',
                        borderRadius: 4,
                        width: 'calc(100% / 5 - 6px)',
                        margin: '2px',
                        padding: '26px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Statistic
                        style={{ justifyContent: 'center' }}
                        title={_item.title}
                        value={_item.value}
                        suffix={_item.suffix}
                        direction={'row'}
                      />
                    </div>
                  ))}
                </div>
              ))}
          </Content>
          {/* === 통계 - end === */}

          {/* ===== 하단컨텐츠 - start ===== */}
          <Row gutter={[1, 1]} className="boxShadow">
            {/* === 회원목록 - start === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <UserList
                  users={filterList}
                  selectedUser={currentUser}
                  fetch={false}
                  onChange={setCurrentUser}
                  // query={{ filter: 'user_name' }}
                  maxHeight={611}
                />
              </Col>
            )}
            {/* === 회원목록 - end === */}

            {/* === 리뷰목록 - start === */}
            <Col x={permission === 'ADMIN' ? 20 : 24}>
              <Content
                maxWidth={'100%'}
                width={1400}
                height={'100%'}
                minHeight={490}
                maxHeight={662}
                borderRadius={4}
                padding={20}
              >
                {/* === 헤더 === */}

                <div>
                  <Title
                    size={4}
                    style={{ marginBottom: 10 }}
                    subButton={
                      <Flex gap={4}>
                        <Button
                          onClick={() => {
                            downloadXlsx({
                              columns: col,
                              rows: currentUserData?.membership_grade?.map(
                                (member) => ({
                                  ...member,
                                  user_name: `${currentUserData?.user_name}(${currentUserData?.user_account})`,
                                  user_phone: currentUserData?.user_phone,
                                })
                              ),
                              fileName: '등급변동내역',
                            });
                          }}
                        >
                          엑셀로 다운로드
                        </Button>
                      </Flex>
                    }
                  >
                    <span>
                      검색결과 총{' '}
                      <a>{currentUserData?.membership_grade?.length ?? 0}</a>건
                    </span>
                  </Title>
                </div>

                {/* === 포인트 목록 === */}
                <Table
                  size="small"
                  pagination={{
                    pageSize: 50,
                  }}
                  scroll={{
                    x: 1250,
                    y: 49 * 10,
                  }}
                  dataSource={currentUserData?.membership_grade}
                  columns={col}
                />
              </Content>
            </Col>
          </Row>
          {/* ===== 하단컨텐츠 - end ===== */}
        </Content>
      </Spin>
      {/* === Loading - start === */}
    </>
  );
};
UserGradeListPresenter.defaultProps = {
  users: [],
};
export default UserGradeListPresenter;
